import React, { useCallback, useEffect, useState } from 'react';
import { elegantMenuProps } from '../../utils/menuStyles';
import {
  Box, Paper, Stack, Typography, TextField,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, Chip, IconButton, Menu, ListItemIcon, ListItemText,
  Tooltip, Skeleton, useTheme, MenuItem as MenuItemMui, LinearProgress,
} from '@mui/material';
import { MoreVert, Payments, MoneyOff, Download, FilterList } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useLetterPdf } from '../letters/useLetterPdf';
import BusyIcon from '../common/BusyIcon';
import finePaymentService from '../../services/finePaymentService';
import { useDebounce } from '../../hooks/useDebounce';
import { LetterV2, LetterPaymentStatus, LetterCategory } from '../../types/letters';
import { severityChipSx, paymentStatusSx } from '../../utils/severityTone';
import RecordPaymentDialog from './RecordPaymentDialog';
import WaiveFineDialog from './WaiveFineDialog';
import { useModulePermissions } from '../../hooks/useModulePermissions';
import DateRangePicker from '../common/DateRangePicker';
import SearchableSelect, { SelectOption } from '../common/SearchableSelect';
import { tagColor } from '../../utils/tagColor';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';
import { useTimezone } from '../../hooks/useTimezone';


const OutstandingFinesTable: React.FC<{ refreshKey?: number; onChanged?: () => void }> = ({ refreshKey, onChanged }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { download: downloadLetter, busyId: downloadingId } = useLetterPdf();
  const { canWrite } = useModulePermissions();
  const canPay = canWrite('fines');
  const fmt = useCurrencyFormat();
  const { formatDate } = useTimezone();
  const [outstandingTotal, setOutstandingTotal] = useState(0);

  const [letters, setLetters] = useState<LetterV2[]>([]);
  const [categories, setCategories] = useState<LetterCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const [search, setSearch] = useState('');
  // 300ms debounce — matches the project default; same hook used elsewhere.
  // Page resets to 0 when the debounced value changes (see effect below) so
  // the user isn't stranded on page 3 of yesterday's filter.
  const debouncedSearch = useDebounce(search, 300);
  const [paymentStatus, setPaymentStatus] = useState<LetterPaymentStatus | 'all'>('all');
  const [categoryId, setCategoryId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuTarget, setMenuTarget] = useState<LetterV2 | null>(null);
  const [payTarget, setPayTarget] = useState<LetterV2 | null>(null);
  const [waiveTarget, setWaiveTarget] = useState<LetterV2 | null>(null);

  const loadCategories = useCallback(async () => {
    try {
      const { categories: list } = await finePaymentService.getFinesContext();
      setCategories(list);
    } catch {}
  }, []);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Server-side filter is now anchored to the outstanding set (active +
      // unpaid|partial). When the user picks a single payment-status we narrow
      // within that set; otherwise the server returns unpaid+partial.
      const res = await finePaymentService.listOutstandingFines({
        category: categoryId || undefined,
        paymentStatus: paymentStatus === 'all' ? undefined : paymentStatus,
        from: from || undefined,
        to: to || undefined,
        search: debouncedSearch,
        page: page + 1,
        limit: rowsPerPage,
      });
      const items = res.items;
      setOutstandingTotal(res.totals?.outstanding ?? 0);
      setLetters(items);
      setTotalCount(res.pagination.totalItems);
    } catch (err: any) {
      enqueueSnackbar(err.message || 'Failed to load fines', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, paymentStatus, categoryId, from, to, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Reset to page 0 whenever the search resolves to a new value — otherwise
  // typing a filter could leave the user pointing at a page that no longer
  // has rows.
  useEffect(() => { setPage(0); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [debouncedSearch]);


  return (
    <Box>
      {/* Filters */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${theme.palette.divider}` }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <FilterList sx={{ color: 'text.disabled', display: { xs: 'none', md: 'block' } }} />
          <TextField
            size="small" placeholder="Search student or reason"
            value={search} onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <SearchableSelect
            label="Category"
            value={categoryId}
            onChange={(v) => { setCategoryId(v); setPage(0); }}
            options={[
              { value: '', label: 'All categories' },
              ...categories.map(c => ({ value: c._id, label: c.label })),
            ] as SelectOption[]}
            placeholder="All categories"
            minWidth={160}
            renderOptionAdornment={(opt) => opt.value ? (
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tagColor(opt.label) }} />
            ) : null}
          />
          <SearchableSelect
            label="Payment"
            value={paymentStatus}
            onChange={(v) => { setPaymentStatus((v || 'all') as any); setPage(0); }}
            options={[
              { value: 'all', label: 'Unpaid + Partial' },
              { value: 'unpaid', label: 'Unpaid only' },
              { value: 'partial', label: 'Partial only' },
            ]}
            clearable={false}
            minWidth={160}
          />

          <DateRangePicker
            label="Date range"
            value={{ from: from || null, to: to || null }}
            onChange={(r) => { setFrom(r.from || ''); setTo(r.to || ''); setPage(0); }}
            minWidth={260}
          />
          <Box sx={{ flex: 1 }} />
          <Stack alignItems={{ md: 'flex-end' }}>
            <Typography variant="caption" color="text.secondary">Outstanding</Typography>
            <Typography variant="h6" fontWeight={700} sx={{ color: theme.palette.error.main }}>{fmt(outstandingTotal)}</Typography>
          </Stack>
        </Stack>
      </Paper>

      {/* Table */}
      <Paper elevation={0} sx={{ borderRadius: 2, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', position: 'relative' }}>
        {/* Stale-while-revalidate strip — visible only during refetch when we
            already have rows to show. Keeps the table body stable; the user
            only ever sees the small progress bar move. */}
        {loading && letters.length > 0 && (
          <LinearProgress
            sx={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1,
            }}
          />
        )}
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Title / Reason</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Issued</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Paid</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Outstanding</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Initial load (no rows yet) → skeleton rows. Subsequent
                  refetches keep the previous rows visible — see the
                  LinearProgress strip above the table. */}
              {loading && letters.length === 0 ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 9 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : letters.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} sx={{ textAlign: 'center', py: 6 }}>
                    <Payments sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">No outstanding fines</Typography>
                    <Typography variant="caption" color="text.disabled">Everyone's paid up.</Typography>
                  </TableCell>
                </TableRow>
              ) : letters.map(l => {
                const out = Math.max(0, (l.amount || 0) - (l.paidAmount || 0) - (l.waivedAmount || 0));
                const studentName = typeof l.studentId === 'string' ? l.studentId : l.studentId?.name;
                const rollNumber = typeof l.studentId === 'string' ? '' : l.studentId?.rollNumber;
                return (
                  <TableRow key={l._id} hover>
                    <TableCell>
                      <Stack>
                        <Typography variant="body2" fontWeight={600}>{studentName || '—'}</Typography>
                        {rollNumber && <Typography variant="caption" color="text.secondary">{rollNumber}</Typography>}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {l.categoryId && (
                        <Chip label={l.categoryId.label} size="small" sx={severityChipSx(theme, l.categoryId.severity)} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.title || l.reason || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{formatDate(l.issuedAt)}</Typography>
                    </TableCell>
                    <TableCell align="right"><Typography variant="body2">{fmt(l.amount ?? 0)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ color: theme.palette.success.main }}>{fmt(l.paidAmount ?? 0)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" fontWeight={600} sx={{ color: theme.palette.error.main }}>{fmt(out)}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        label={l.paymentStatus || 'unpaid'}
                        size="small"
                        sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600, ...paymentStatusSx(theme, l.paymentStatus) }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="More actions">
                        <IconButton size="small" onClick={(e) => { setMenuAnchor(e.currentTarget); setMenuTarget(l); }}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Row menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        {...elegantMenuProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
        {canPay && (
          <MenuItemMui onClick={() => { setPayTarget(menuTarget); setMenuAnchor(null); }}>
            <ListItemIcon><Payments /></ListItemIcon>
            <ListItemText primary="Record payment" />
          </MenuItemMui>
        )}
        {canPay && (
          <MenuItemMui onClick={() => { setWaiveTarget(menuTarget); setMenuAnchor(null); }}>
            <ListItemIcon><MoneyOff /></ListItemIcon>
            <ListItemText primary="Waive fine" />
          </MenuItemMui>
        )}
        <MenuItemMui onClick={async () => {
          if (downloadingId) return;
          if (menuTarget) {
            await downloadLetter(menuTarget._id);
          }
          setMenuAnchor(null);
        }}>
          <ListItemIcon><BusyIcon busy={!!downloadingId} size={18}><Download /></BusyIcon></ListItemIcon>
          <ListItemText primary={downloadingId ? 'Preparing…' : 'Download PDF'} />
        </MenuItemMui>
      </Menu>

      <RecordPaymentDialog
        open={Boolean(payTarget)}
        onClose={() => setPayTarget(null)}
        letter={payTarget}
        onRecorded={() => { load(); onChanged?.(); }}
      />
      <WaiveFineDialog
        open={Boolean(waiveTarget)}
        onClose={() => setWaiveTarget(null)}
        letter={waiveTarget}
        onWaived={() => { load(); onChanged?.(); }}
      />
    </Box>
  );
};

export default OutstandingFinesTable;
